import React, { useState, useEffect } from "react";
import {
    Search, RefreshCw, AlertTriangle, CheckCircle2,
    Clock, Tag, FileText, ChevronLeft, ChevronRight,
    ChevronsLeft, ChevronsRight, Info
} from "lucide-react";
import baseapi from "../../../services/baseapi"; // Adjust import path
import { showError } from "../../../utils/toast"; // Adjust import path
import { ItemsPerPageDropdown } from "../../../components/ItemsPerPageDropdown "; // Adjust import path

interface IloEventLog {
    id: number;
    asset_id: number;
    event_id: string;
    timestamp: string;
    severity: string;
    category: string;
    message_id: string;
    message: string;
}

interface AssetIloEventLogsProps {
    assetId: string | number;
}

export default function AssetIloEventLogs({ assetId }: AssetIloEventLogsProps) {
    const [logs, setLogs] = useState<IloEventLog[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [limit, setLimit] = useState(15);
    const [totalRecords, setTotalRecords] = useState(0);

    const fetchLogs = async () => {
        setLoading(true);
        try {
            const skip = (currentPage - 1) * limit;

            const queryObj: any = {
                asset_id: assetId
            };

            if (searchTerm) {
                queryObj.message = { $ilike: `%${searchTerm}%` };
            }

            const payload: any = {
                skip,
                limit,
                query: JSON.stringify(queryObj)
            };

            const response = await baseapi.get("/asset_ilo_event_logs", { params: payload });

            if (response.data.status) {
                setLogs(response.data.data);
                setTotalRecords(response.data.total || response.data.data.length || 0);
            } else {
                showError(response.data.message || "Failed to load event logs");
            }
        } catch (error: any) {
            showError(error.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (assetId) {
            fetchLogs();
        }
    }, [currentPage, limit, assetId]);

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault();
        setCurrentPage(1);
        fetchLogs();
    };

    const renderSeverityBadge = (severity: string) => {
        if (!severity) return <span className="text-slate-400 dark:text-gray-500">-</span>;

        const s = severity.toLowerCase();

        if (s === "critical" || s === "fatal" || s === "error") {
            return (
                <div className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase bg-rose-50 dark:bg-red-500/10 text-rose-700 dark:text-red-400 border border-rose-200 dark:border-red-500/20 whitespace-nowrap">
                    <AlertTriangle size={10} /> {severity}
                </div>
            );
        } else if (s === "warning") {
            return (
                <div className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase bg-amber-50 dark:bg-yellow-500/10 text-amber-700 dark:text-yellow-400 border border-amber-200 dark:border-yellow-500/20 whitespace-nowrap">
                    <AlertTriangle size={10} /> {severity}
                </div>
            );
        } else if (s === "ok" || s === "info" || s === "good") {
            return (
                <div className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-500/20 whitespace-nowrap">
                    <CheckCircle2 size={10} /> {severity}
                </div>
            );
        }

        return (
            <div className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase bg-slate-100 dark:bg-gray-800 text-slate-700 dark:text-gray-300 border border-slate-200 dark:border-gray-700 whitespace-nowrap">
                <Info size={10} /> {severity}
            </div>
        );
    };

    return (
        <div className="flex flex-col text-slate-900 dark:text-gray-200 relative overflow-hidden pb-4 px-2">
            {/* Toolbar */}
            <div className="py-2 flex items-center justify-between gap-4 shrink-0 mb-3 border-b border-slate-200 dark:border-gray-700 pb-2">
                <div className="flex items-center">
                    <div className="p-1.5 bg-orange-600 rounded mr-2.5 shadow-sm">
                        <FileText className="w-4 h-4 text-white" />
                    </div>
                    <div>
                        <h2 className="text-sm font-bold text-slate-900 dark:text-white leading-tight">iLO Event Logs</h2>
                    </div>
                </div>
                
                <div className="flex items-center gap-2">
                    <form onSubmit={handleSearch} className="relative w-64 group">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 dark:text-gray-200 group-focus-within:text-indigo-600 dark:group-focus-within:text-indigo-400 transition-colors" />
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Search messages..."
                            className="w-full bg-white dark:bg-gray-900 border border-slate-200 dark:border-gray-700 rounded-lg pl-8 pr-3 py-1.5 text-[11px] text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-gray-500 focus:outline-none focus:border-indigo-500 transition-all shadow-sm"
                        />
                    </form>
                    <button
                        onClick={() => { setCurrentPage(1); fetchLogs(); }}
                        disabled={loading}
                        className="p-1.5 bg-white dark:bg-gray-800 border border-slate-200 dark:border-gray-700 rounded-lg text-slate-700 dark:text-gray-300 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-gray-700 transition-all group shrink-0 shadow-sm cursor-pointer"
                    >
                        <RefreshCw size={14} className={loading ? 'animate-spin' : 'group-hover:text-indigo-500'} />
                    </button>
                </div>
            </div>

            {/* Table Container */}
            <div className="bg-white dark:bg-gray-800/40 border border-slate-200 dark:border-gray-700 rounded-xl overflow-hidden shadow-sm dark:shadow-2xl flex flex-col max-h-full">
                <div className="relative overflow-x-auto overflow-y-auto custom-scroll">
                    <table className="w-full text-left border-collapse table-fixed">
                        <thead className="bg-slate-100 dark:bg-gray-800/80 sticky top-0 z-10">
                            <tr className="border-b border-slate-200 dark:border-gray-700/50">
                                <th className="w-[10%] px-4 py-2 text-[10px] font-bold text-slate-700 dark:text-gray-100 uppercase tracking-widest whitespace-nowrap">
                                    <div className="flex items-center gap-2">
                                        <Tag size={12} className="text-indigo-600 dark:text-indigo-400 shrink-0" /> <span>Event ID</span>
                                    </div>
                                </th>
                                <th className="w-[12%] px-4 py-2 text-[10px] font-bold text-slate-700 dark:text-gray-100 uppercase tracking-widest whitespace-nowrap">
                                    Severity
                                </th>
                                <th className="w-[18%] px-4 py-2 text-[10px] font-bold text-slate-700 dark:text-gray-100 uppercase tracking-widest whitespace-nowrap">
                                    <div className="flex items-center gap-2">
                                        <Clock size={12} className="text-amber-500 dark:text-yellow-500 shrink-0" /> <span>Timestamp</span>
                                    </div>
                                </th>
                                <th className="w-[15%] px-4 py-2 text-[10px] font-bold text-slate-700 dark:text-gray-100 uppercase tracking-widest whitespace-nowrap">
                                    Category
                                </th>
                                <th className="w-[10%] px-4 py-2 text-[10px] font-bold text-slate-700 dark:text-gray-100 uppercase tracking-widest whitespace-nowrap">
                                    Msg ID
                                </th>
                                <th className="w-[35%] px-4 py-2 text-[10px] font-bold text-slate-700 dark:text-gray-100 uppercase tracking-widest whitespace-nowrap">
                                    Message
                                </th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-200 dark:divide-gray-700/60 bg-white dark:bg-gray-900/20 text-xs">
                            {loading && logs.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="py-12 text-center">
                                        <div className="flex flex-col items-center justify-center space-y-2">
                                            <div className="w-6 h-6 border-2 border-indigo-500/20 border-t-indigo-500 rounded-full animate-spin"></div>
                                            <p className="text-[9px] font-black text-slate-500 dark:text-gray-200 uppercase tracking-widest">Fetching Logs...</p>
                                        </div>
                                    </td>
                                </tr>
                            ) : logs.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="py-12 text-center">
                                        <div className="flex flex-col items-center justify-center space-y-2 opacity-40">
                                            <FileText size={24} className="text-slate-400 dark:text-gray-400" />
                                            <p className="text-[9px] font-bold text-slate-600 dark:text-gray-200 uppercase tracking-widest">No event logs found</p>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                logs.map((log) => (
                                    <tr key={log.id} className="hover:bg-slate-50 dark:hover:bg-gray-800/40 transition-colors group">
                                        <td className="px-4 py-2 whitespace-nowrap font-mono text-slate-700 dark:text-gray-300">
                                            {log.event_id || "-"}
                                        </td>
                                        <td className="px-4 py-2 whitespace-nowrap">
                                            {renderSeverityBadge(log.severity)}
                                        </td>
                                        <td className="px-4 py-2 whitespace-nowrap font-mono text-[10px] text-slate-600 dark:text-gray-200">
                                            {log.timestamp ? new Date(log.timestamp + "Z").toLocaleString() : "-"}
                                        </td>
                                        <td className="px-4 py-2 whitespace-nowrap text-[11px] text-slate-700 dark:text-gray-300">
                                            {log.category || "-"}
                                        </td>
                                        <td className="px-4 py-2 whitespace-nowrap font-mono text-[10px] text-indigo-600 dark:text-indigo-400 font-semibold">
                                            {log.message_id || "-"}
                                        </td>
                                        <td className="px-4 py-2 whitespace-nowrap">
                                            {log.message ? (
                                                <span 
                                                    className="text-[10px] text-slate-700 dark:text-gray-200 leading-tight" 
                                                    title={log.message}
                                                >
                                                    {log.message.length > 100
                                                        ? `${log.message.substring(0, 100)}...` 
                                                        : log.message}
                                                </span>
                                            ) : (
                                                <span className="text-slate-400 dark:text-gray-500">-</span>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination Footer */}
                <div className="flex items-center justify-between px-3 py-1.5 bg-slate-50 dark:bg-gray-800/40 border-t border-slate-200 dark:border-gray-700 shrink-0 mt-auto">
                    <div className="flex items-center gap-3">
                        <ItemsPerPageDropdown
                            value={limit}
                            onChange={(v) => {
                                setLimit(v);
                                setCurrentPage(1);
                            }}
                        />
                        <span className="text-[9px] font-medium text-slate-600 dark:text-gray-200 tracking-widest">
                            <span className="text-slate-900 dark:text-gray-300 font-semibold">
                                {totalRecords === 0 ? 0 : (currentPage - 1) * limit + 1}
                            </span>
                            {" - "}
                            <span className="text-slate-900 dark:text-gray-300 font-semibold">
                                {Math.min(currentPage * limit, totalRecords)}
                            </span>
                            <span className="mx-1 text-slate-400 dark:text-gray-600">/</span>
                            <span className="text-slate-900 dark:text-gray-300 font-bold">{totalRecords}</span>
                        </span>
                    </div>

                    <div className="flex items-center gap-1">
                        <button
                            disabled={currentPage === 1}
                            onClick={() => setCurrentPage(1)}
                            className="p-1 text-slate-600 dark:text-gray-200 hover:text-slate-900 dark:hover:text-white disabled:opacity-20 transition-colors bg-white dark:bg-gray-800/50 hover:bg-slate-100 dark:hover:bg-gray-700 border border-slate-200 dark:border-transparent rounded cursor-pointer"
                        >
                            <ChevronsLeft size={14} />
                        </button>
                        <button
                            disabled={currentPage === 1}
                            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                            className="p-1 text-slate-600 dark:text-gray-200 hover:text-slate-900 dark:hover:text-white disabled:opacity-20 transition-colors bg-white dark:bg-gray-800/50 hover:bg-slate-100 dark:hover:bg-gray-700 border border-slate-200 dark:border-transparent rounded cursor-pointer"
                        >
                            <ChevronLeft size={14} />
                        </button>
                        <div className="px-2 py-0.5 bg-indigo-50 dark:bg-indigo-500/10 border border-indigo-200 dark:border-indigo-500/20 text-indigo-700 dark:text-indigo-400 rounded text-[10px] font-bold mx-1">
                            {currentPage}
                        </div>
                        <button
                            disabled={currentPage * limit >= totalRecords}
                            onClick={() => setCurrentPage(prev => prev + 1)}
                            className="p-1 text-slate-600 dark:text-gray-200 hover:text-slate-900 dark:hover:text-white disabled:opacity-20 transition-colors bg-white dark:bg-gray-800/50 hover:bg-slate-100 dark:hover:bg-gray-700 border border-slate-200 dark:border-transparent rounded cursor-pointer"
                        >
                            <ChevronRight size={14} />
                        </button>
                        <button
                            disabled={currentPage * limit >= totalRecords}
                            onClick={() => setCurrentPage(Math.ceil(totalRecords / limit))}
                            className="p-1 text-slate-600 dark:text-gray-200 hover:text-slate-900 dark:hover:text-white disabled:opacity-20 transition-colors bg-white dark:bg-gray-800/50 hover:bg-slate-100 dark:hover:bg-gray-700 border border-slate-200 dark:border-transparent rounded cursor-pointer"
                        >
                            <ChevronsRight size={14} />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}